import React, { useContext, useState, useEffect, Fragment } from "react";
import { equals, map } from "ramda";
import { UserContext } from "UserContext";
import { Box, Grid, makeStyles } from "@material-ui/core";
import DateSelectionCard from "./DateSelectionCard";
import OrganizationSelectionCard from "./OrganizationSelectionCard";
import ModuleSelectionCard from "./ModuleSelectionCard";
import { allTrue, isBlank, notBlank } from "./utils";
import { postRequest } from "./fetchUtils";
import SuccessDialog from "./SuccessDialog";
import ContentTypeCard from "ContentTypeCard";

export const REPORT_TYPES = {
  KBART: "KBART",
  MARC: "MARC",
};

// Open Research Library org id
const ORL = "1f7368e7-f10b-49a1-8ced-2d9476279974";

/**
 * returns modules that are found in content_sync table, i.e. those with sync data to be used in reports
 * this route returns modules scoped by org that are ACTIVE, visible, and not excluded from catalog
 */
const getModuleUrl = orgId =>
  `${process.env.REACT_APP_API_BASE_URL}/integration/opds/modules?org-id=${orgId}&limit=all`;

const fetchModules = (orgId, token) =>
  fetch(getModuleUrl(orgId), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

const mapModule = module => ({ id: module.id, name: module.displayName });

const useStyles = makeStyles(theme => ({
  root: {
    background: theme.palette.secondary.light,
    height: "100%",
    overflowX: "hidden",
  },

  errorHeading: {
    color: theme.palette.error.main,
    fontSize: 14,
    marginBottom: 5,
  },

  errorMessage: {
    color: theme.palette.error.main,
    marginBottom: 10,
  },
}));

const initialState = {
  showLoading: false,
  showSuccessDialog: false,
  disableExport: false,
  selectedOrganization: "",
  useCustomSubdomain: true, // for now, leave defaulted to true for KBART reports
  emailAddress: "",
  startDate: null,
  endDate: null,
  modules: [],
  includeDelete: false,
  contentTypes: [],
};

const Export = ({ reportType, getExportGenerationUrl }) => {
  const classes = useStyles();
  const { token, user } = useContext(UserContext);
  const [state, setState] = useState(initialState);
  const [error, setError] = useState(null);

  const [modules, setModules] = useState([]);
  useEffect(() => {
    if (notBlank(user.organizations) && isBlank(state.selectedOrganization)) {
      handleStateChange("selectedOrganization")(user.organizations[0].id);
    }

    if (token && notBlank(state.selectedOrganization)) {
      fetchModules(state.selectedOrganization, token)
        .then(response => response.json())
        .then(modules => map(mapModule, modules))
        .then(modules => modules.sort((a, b) => a.name.localeCompare(b.name)))
        .then(modules => setModules(modules));
    } else {
      setModules([]);
    }
  }, [token, user.id, user.organizations, state.selectedOrganization]);

  const handleStateChange = name => value => {
    setState(s => ({ ...s, [name]: value }));
  };

  useEffect(() => {
    const startDate = state.startDate;
    const endDate = state.endDate;

    if (notBlank(startDate) && notBlank(endDate)) {
      handleStateChange("disableExport")(startDate > endDate);
    }

    handleStateChange("disableExport")(isBlank(state.emailAddress));
  }, [state.startDate, state.endDate, state.emailAddress]);

  const exportReport = props => {
    handleStateChange("showLoading")(true);

    const url = getExportGenerationUrl(state.selectedOrganization);
    const request = {
      email: state.emailAddress,
      organizationId: state.selectedOrganization,
      startDate: state.startDate,
      endDate: state.endDate,
      moduleMediaIds: state.modules,
      contentTypes: state.contentTypes,
      useCustomSubdomain: state.useCustomSubdomain,
      currentOnly: !state.includeDelete,
    };

    // Manually set ORL host url for KBART "online_identifier"
    if (
      allTrue([
        equals(reportType, REPORT_TYPES.KBART),
        equals(state.selectedOrganization, ORL),
      ])
    ) {
      request.hostUrl = "https://openresearchlibrary.org";
    }

    postRequest(url, props.token, request)
      .then(() => handleStateChange("showSuccessDialog")(true))
      .catch(err => setError(err.message))
      .finally(() => handleStateChange("showLoading")(false));
  };

  return (
    <Fragment>
      {isBlank(modules) ? (
        <Grid item>
          <div>Loading...</div>
        </Grid>
      ) : notBlank(error) ? (
        <Grid item>
          <Box className={classes.errorHeading}>
            Error Requesting {reportType} Generation.
          </Box>
          <Box className={classes.errorMessage}>{error}</Box>
          <Box>
            {"If you continue to have any issues, please contact "}
            <a href="mailto:support@biblioboard.com">support@biblioboard.com</a>
          </Box>
        </Grid>
      ) : (
        <Fragment>
          <Grid item>
            <OrganizationSelectionCard
              reportType={reportType}
              state={state}
              handleStateChange={handleStateChange}
              organizations={user.organizations}
            />
          </Grid>

          <Grid item>
            <DateSelectionCard
              reportType={reportType}
              state={state}
              handleStateChange={handleStateChange}
            />
          </Grid>

          <Grid item>
            <ContentTypeCard
              reportType={reportType}
              state={state}
              handleStateChange={handleStateChange}
            />
          </Grid>

          <Grid item>
            <ModuleSelectionCard
              reportType={reportType}
              state={state}
              handleStateChange={handleStateChange}
              modules={modules}
              handleExportClicked={exportReport}
            />
          </Grid>
        </Fragment>
      )}
      <SuccessDialog
        open={state.showSuccessDialog}
        message={`Request for ${reportType} has been sent. Check email for download link.`}
        handleClose={() => handleStateChange("showSuccessDialog")(false)}
      />
    </Fragment>
  );
};

export default Export;
